/**
 * HailProtector Variants Module - v1.0.0
 * Native Shopify Variant Integration
 * 
 * Features:
 * - Parse product JSON for variant data
 * - URL deep linking (?variant=ID)
 * - Browser history (back/forward)
 * - Dispatch variant:change events
 * - Sync hidden variant select
 * 
 * Dependencies: None (standalone)
 */

window.HailProtector = window.HailProtector || {};

window.HailProtector.Variants = (function() {
  'use strict';
  
  /**
   * Create a variants handler instance
   * @param {HTMLElement} container - Form container element
   * @param {Object} options - Configuration options
   * @param {Function} options.onChange - Callback when variant changes
   * @param {Function} options.onHistoryChange - Callback for popstate
   * @returns {Object} Variants API
   */
  function create(container, options) {
    if (!container) return null;
    
    options = options || {};
    
    // ========================================
    // PRODUCT DATA
    // ========================================
    var productData = null;
    var variantsMap = {};
    
    var productJsonEl = container.querySelector('[data-product-json]');
    if (productJsonEl) {
      try {
        productData = JSON.parse(productJsonEl.textContent);
        if (productData.variants) {
          for (var i = 0; i < productData.variants.length; i++) {
            var v = productData.variants[i];
            variantsMap[String(v.id)] = v;
          }
        }
      } catch (e) {
        console.warn('HailProtector Variants: Could not parse product JSON', e);
      }
    }
    
    // ========================================
    // DOM REFERENCES
    // ========================================
    var variantSelect = container.querySelector('[data-variant-select]');
    var form = container.querySelector('[data-product-form]');
    
    // ========================================
    // STATE
    // ========================================
    var currentVariantId = null;
    
    // ========================================
    // VARIANT LOOKUP
    // ========================================
    
    /**
     * Find variant by size code and color
     * @param {string} size - Size code (CAR1, SUV2, etc.)
     * @param {string} color - Color name (silver, black)
     * @returns {Object|null} Variant object or null
     */
    function findByOptions(size, color) {
      if (!productData || !productData.variants) return null;
      
      for (var i = 0; i < productData.variants.length; i++) {
        var v = productData.variants[i];
        var vSize = v.option1 || '';
        var vColor = (v.option2 || 'silver').toLowerCase();
        
        if (vSize.indexOf(size) !== -1 && vColor === color) {
          return v;
        }
      }
      return null;
    }
    
    /**
     * Find variant by ID
     * @param {string|number} variantId
     * @returns {Object|null}
     */
    function findById(variantId) {
      return variantsMap[String(variantId)] || null;
    }
    
    /**
     * Extract size and color from variant
     * @param {Object} variant
     * @returns {Object|null} {size, color, system}
     */
    function parseVariant(variant) {
      if (!variant) return null;
      
      var size = variant.option1 || '';
      var color = (variant.option2 || 'silver').toLowerCase();
      
      // Extract size code
      var sizeMatch = size.match(/CAR[1-3]|SUV[1-3]/);
      var sizeCode = sizeMatch ? sizeMatch[0] : size;
      var system = sizeCode.indexOf('CAR') === 0 ? 'sentinel' : 'guardian';
      
      return { size: sizeCode, color: color, system: system };
    }
    
    // ========================================
    // EVENTS
    // ========================================
    
    /**
     * Dispatch variant:change event
     * @param {Object} variant - Selected variant object
     */
    function dispatchChange(variant) {
      if (!variant) return;
      
      currentVariantId = variant.id;
      
      // Standard Shopify variant change event
      var event = new CustomEvent('variant:change', {
        bubbles: true,
        detail: {
          variant: variant,
          sectionId: container.dataset.sectionId || null
        }
      });
      document.dispatchEvent(event);
      
      // Also dispatch on form for compatibility
      if (form) {
        form.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: variant }
        }));
      }
      
      // Callback
      if (options.onChange) {
        options.onChange(variant);
      }
    }
    
    // ========================================
    // URL MANAGEMENT
    // ========================================
    
    /**
     * Update URL with variant for deep linking
     * @param {Object} variant
     * @param {boolean} replaceState - Use replaceState vs pushState
     */
    function updateURL(variant, replaceState) {
      if (!variant) return;
      
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      
      // Preserve system param for backwards compat
      var parsed = parseVariant(variant);
      if (parsed && parsed.system) {
        url.searchParams.set('system', parsed.system);
      }
      
      var method = replaceState ? 'replaceState' : 'pushState';
      window.history[method]({ variantId: variant.id }, '', url.toString());
    }
    
    /**
     * Get variant ID from URL
     * @returns {string|null}
     */
    function getURLVariantId() {
      var params = new URLSearchParams(window.location.search);
      return params.get('variant');
    }
    
    /**
     * Get system from URL (legacy support)
     * @returns {string|null}
     */
    function getURLSystem() {
      var params = new URLSearchParams(window.location.search);
      var system = params.get('system');
      return (system === 'guardian' || system === 'sentinel') ? system : null;
    }
    
    // ========================================
    // SYNC
    // ========================================
    
    /**
     * Sync hidden variant select element
     * @param {Object} variant
     */
    function syncSelect(variant) {
      if (!variantSelect || !variant) return;
      
      for (var i = 0; i < variantSelect.options.length; i++) {
        if (variantSelect.options[i].value === String(variant.id)) {
          variantSelect.selectedIndex = i;
          break;
        }
      }
    }
    
    /**
     * Select variant by size and color
     * @param {string} size - Size code
     * @param {string} color - Color name
     * @param {boolean} [updateHistory=true]
     * @returns {Object|null} Selected variant
     */
    function select(size, color, updateHistory) {
      if (updateHistory === undefined) updateHistory = true;
      
      var variant = findByOptions(size, color);
      if (!variant) return null;
      
      syncSelect(variant);
      dispatchChange(variant);
      
      if (updateHistory) {
        updateURL(variant, false);
      }
      
      return variant;
    }
    
    /**
     * Select variant by ID
     * @param {string|number} variantId
     * @param {boolean} [updateHistory=true]
     * @returns {Object|null} Selected variant
     */
    function selectById(variantId, updateHistory) {
      if (updateHistory === undefined) updateHistory = true;
      
      var variant = findById(variantId);
      if (!variant) return null;
      
      syncSelect(variant);
      dispatchChange(variant);
      
      if (updateHistory) {
        updateURL(variant, true);
      }
      
      return variant;
    }
    
    // ========================================
    // HISTORY
    // ========================================
    
    function handlePopstate(e) {
      var variantId = null;
      
      if (e.state && e.state.variantId) {
        variantId = e.state.variantId;
      } else {
        variantId = getURLVariantId();
      }
      
      if (variantId) {
        var variant = findById(variantId);
        if (variant && options.onHistoryChange) {
          options.onHistoryChange(variant);
        }
      }
    }
    
    // ========================================
    // INIT
    // ========================================
    
    function init() {
      window.addEventListener('popstate', handlePopstate);
    }
    
    function destroy() {
      window.removeEventListener('popstate', handlePopstate);
    }
    
    init();
    
    // ========================================
    // PUBLIC API
    // ========================================
    return {
      // Lookup
      findByOptions: findByOptions,
      findById: findById,
      parseVariant: parseVariant,
      
      // Selection
      select: select,
      selectById: selectById,
      
      // URL
      getURLVariantId: getURLVariantId,
      getURLSystem: getURLSystem,
      updateURL: updateURL,
      
      // Sync
      syncSelect: syncSelect,
      dispatchChange: dispatchChange,
      
      // State
      getCurrentId: function() { return currentVariantId; },
      getProductData: function() { return productData; },
      
      // Cleanup
      destroy: destroy
    };
  }
  
  // Module API
  return {
    create: create
  };
})();
